import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Card from "react-bootstrap/esm/Card";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Alert from "react-bootstrap/esm/Alert";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import { Absence, formatAbsence, toIsoDay } from "../utils/absences";

// Keeps the browser's date field from accepting years with more than four digits.
const MAX_DATE = "9999-12-31";

interface MyPlanAbsencesProps {
    token: string;
    absences: Absence[];
    onChange: (absences: Absence[]) => void;
}

/** The preacher's own absences: periods in which they cannot be scheduled. */
export default function MyPlanAbsences({ token, absences, onChange }: MyPlanAbsencesProps) {
    const { t } = useTranslation();
    const today = toIsoDay(new Date());
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const run = async (name: string, data: object) => {
        setBusy(true);
        setError("");
        try {
            const call = httpsCallable<object, { absences: Absence[] }>(functions, name);
            onChange((await call({ token, ...data })).data.absences);
            return true;
        } catch {
            setError(t("myPlan.absenceFailed") as string);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (await run("saveMyAbsence", { from, to: to || from, note: note.trim() })) {
            setFrom("");
            setTo("");
            setNote("");
        }
    };

    return (
        <Card className="mb-3">
            <Card.Body>
                <h5>{t("myPlan.absencesHeading")}</h5>
                <p className="text-muted small">{t("myPlan.absencesIntro")}</p>

                {absences.length > 0 && (
                    <ul className="list-unstyled mb-3">
                        {absences.map((absence) => (
                            <li key={absence.id} className="d-flex justify-content-between align-items-center gap-2 py-1 border-bottom">
                                <span>
                                    {formatAbsence(absence, t("calendar.locale") as string)}
                                    {absence.note && <span className="text-muted"> · {absence.note}</span>}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => run("deleteMyAbsence", { absenceId: absence.id })}
                                >
                                    {t("admin.delete")}
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}

                <Form onSubmit={handleAdd}>
                    <Row className="g-2">
                        <Col xs={6}>
                            <Form.Group controlId="absenceFrom">
                                <Form.Label>{t("myPlan.absenceFrom")}</Form.Label>
                                <Form.Control type="date" value={from} min={today} max={MAX_DATE} onChange={(e) => setFrom(e.target.value)} required />
                            </Form.Group>
                        </Col>
                        <Col xs={6}>
                            <Form.Group controlId="absenceTo">
                                <Form.Label>{t("myPlan.absenceTo")}</Form.Label>
                                <Form.Control type="date" value={to} min={from || today} max={MAX_DATE} onChange={(e) => setTo(e.target.value)} />
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <Form.Group controlId="absenceNote">
                                <Form.Label>{t("myPlan.absenceNote")}</Form.Label>
                                <Form.Control type="text" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
                            </Form.Group>
                        </Col>
                    </Row>
                    {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
                    <Button type="submit" variant="outline-primary" className="mt-3" disabled={busy || !from}>
                        {t("myPlan.absenceAdd")}
                    </Button>
                </Form>
            </Card.Body>
        </Card>
    );
}
